import React from 'react';

/**
 * PolicyPipeline — six-stage policy enforcement pipeline visualisation
 * for a single execution event.
 *
 * Stages (per the AGENT_EXECUTION_MODEL):
 *   0 INIT   — request received, ready
 *   1 PRE    — pre-execution policy gate
 *   2 EXEC   — skill / tool invocation
 *   3 POST   — post-execution policy gate
 *   4 SEAL   — audit hash chain sealed
 *   5 DONE   — execution complete
 *
 * Each stage shows its outcome (passed / warned / blocked / current /
 * future) as a coloured square. Compact mode renders just the squares;
 * full mode adds stage labels below for the expanded execution detail.
 *
 * Phase A9 / G309 — used in LiveExecutionTab.
 *
 * @param {Object} props
 * @param {Array<{ name?:string, result?:string, scope?:string, violation?:string }>} props.stages
 * @param {'mcp'|'gel'} [props.type='gel']     — MCP renders a single square
 * @param {'compact'|'full'} [props.variant='compact']
 * @param {number} [props.currentIndex]        — which stage is in-flight (for animation)
 */
export function PolicyPipeline({ stages = [], type = 'gel', variant = 'compact', currentIndex }) {
  if (type === 'mcp') {
    // MCP invocations are a single tool call — show one square that
    // reflects the overall result.
    const result = (stages?.[0]?.result || 'passed').toLowerCase();
    return (
      <div className="inline-flex items-center gap-1.5">
        <StageSquare result={result} compact={variant === 'compact'} />
        {variant === 'full' && (
          <span className="text-[10.5px] text-v2-ink-muted">MCP tool call</span>
        )}
      </div>
    );
  }

  const STAGE_DEFS = [
    { idx: 0, key: 'init', label: 'Init',  desc: 'Request received' },
    { idx: 1, key: 'pre',  label: 'Pre',   desc: 'Pre-execution gate' },
    { idx: 2, key: 'exec', label: 'Exec',  desc: 'Skill invocation' },
    { idx: 3, key: 'post', label: 'Post',  desc: 'Post-execution gate' },
    { idx: 4, key: 'seal', label: 'Seal',  desc: 'Audit hash sealed' },
    { idx: 5, key: 'done', label: 'Done',  desc: 'Complete' },
  ];

  // Map provided stages by index for quick lookup. Missing indices
  // render as the "future" state (light grey, no result yet).
  const stageMap = new Map();
  for (let i = 0; i < stages.length; i += 1) {
    stageMap.set(i, stages[i]);
  }

  return (
    <div className={`inline-flex ${variant === 'full' ? 'flex-col' : 'items-center'} gap-1.5`}>
      <div className="inline-flex items-center gap-1">
        {STAGE_DEFS.map((def) => {
          const data = stageMap.get(def.idx) || {};
          const result = (data.result || (currentIndex != null && def.idx === currentIndex ? 'current' : (currentIndex != null && def.idx > currentIndex ? 'future' : 'passed'))).toLowerCase();
          return (
            <StageSquare
              key={def.key}
              result={result}
              compact={variant === 'compact'}
              title={`${def.label} — ${data.violation || data.name || def.desc}`}
            />
          );
        })}
      </div>
      {variant === 'full' && (
        <div className="inline-flex items-center gap-1">
          {STAGE_DEFS.map((def) => (
            <div key={def.key} className="text-[9.5px] text-v2-ink-muted text-center w-4">
              {def.label[0]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Single stage square — coloured by result. Pulses when current. */
function StageSquare({ result, compact, title }) {
  const size = compact ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const map = {
    passed:  'bg-v2-success',
    warned:  'bg-v2-warning',
    blocked: 'bg-v2-danger',
    current: 'bg-v2-action animate-pulse',
    future:  'bg-v2-surface-muted',
    skipped: 'bg-v2-surface-muted opacity-60',
  };
  const cls = map[result] || map.future;
  return <span className={`${size} rounded-sm ${cls}`} title={title} aria-label={title} />;
}

export default PolicyPipeline;
